import React, { useEffect, useState } from "react";
import { DateInput } from "@mantine/dates";
import type { FieldComponentProps } from "@/pages/ClientProfile/types";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { showFieldError } from "@utils/reviewFieldUtils.ts";
import { Loader } from "@mantine/core";

interface DateFieldProps extends FieldComponentProps {
  mutationType?: "profile" | "ocr";
  formId?: string;
}

const DateField: React.FC<DateFieldProps> = ({
  commonProps,
  field,
  mutationType: _mutationType = "profile",
  formId: _formId,
}) => {
  const { userId } = useParams();
  const updateProfileField = useProfileStore(
    (state) => state.updateProfileField
  );

  const [loadingState, setLoadingState] = useState(false);
  const [optimisticDate, setOptimisticDate] = useState<Date | null>(
    field.value ? dayjs(field.value, "MM/DD/YYYY").toDate() : null
  );

  useEffect(() => {
    setOptimisticDate(
      field.value ? dayjs(field.value, "MM/DD/YYYY").toDate() : null
    );
  }, [field.value]);

  const handleChange = (value: string | null): void => {
    const newValue =
      value === null ? null : dayjs(value, "MM/DD/YYYY").toDate();
    setOptimisticDate(newValue);
    field.onChange(value);
  };

  const handleBlur = async (): Promise<void> => {
    setLoadingState(true);

    const formattedValue = optimisticDate
      ? dayjs(optimisticDate).format("MM/DD/YYYY")
      : "";

    try {
      await updateProfileField(userId ?? "", field.name, formattedValue);
      // Success notification is shown by the store method
      setLoadingState(false);
    } catch (error: any) {
      showFieldError(error, field.name);
      setOptimisticDate(
        field.value ? dayjs(field.value, "MM/DD/YYYY").toDate() : null
      );
      setLoadingState(false);
    }

    field.onBlur();
  };

  return (
    <DateInput
      {...commonProps}
      {...field}
      value={optimisticDate}
      onChange={handleChange}
      onBlur={handleBlur}
      variant="filled"
      valueFormat="MM/DD/YYYY"
      disabled={loadingState}
      rightSection={
        loadingState || commonProps.rightSection ? <Loader size="xs" /> : null
      }
    />
  );
};

export default DateField;
